import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  TextFieldProps,
} from '@mui/material';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';

export interface ClaudeModelOption {
  id: string;
  label?: string;
}

interface ModelSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  testId?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

function ModelSelect({
  value,
  onChange,
  label = 'Claude model',
  disabled = false,
  testId,
  textFieldProps,
}: ModelSelectProps) {
  const [models, setModels] = useState<ClaudeModelOption[]>([]);
  const [loading, setLoading] = useState(true);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWrapper.get(urls.DevOps.aiClaudeModels);
      setModels(Array.isArray(data) ? data : []);
    } catch {
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModels().catch(() => undefined);
  }, [loadModels]);

  const options = models.map((m) => m.id);
  if (value && !options.includes(value)) {
    options.unshift(value);
  }

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      loading={loading}
      options={options}
      value={value}
      onChange={(_e, newValue) => onChange(newValue ?? '')}
      onInputChange={(_e, newInput) => {
        if (_e?.type === 'change') {
          onChange(newInput);
        }
      }}
      getOptionLabel={(option) => {
        const match = models.find((m) => m.id === option);
        if (match?.label) return `${match.label} (${match.id})`;
        return option;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          fullWidth
          label={label}
          inputProps={{
            ...params.inputProps,
            'data-testid': testId,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ModelSelect;
